<template>
  <div class="vc-wrap">
    <div class="vc-chips">
      <span
        v-for="(v, i) in varieties"
        :key="i"
        class="vc-chip"
      >
        {{ v.name }}
        <button class="vc-chip-remove" @click="remove(i)" type="button" :aria-label="t('varietyChooser.remove')">×</button>
      </span>

      <div class="vc-input-wrap">
        <input
          ref="inputRef"
          v-model="draft"
          class="vc-input"
          :placeholder="varieties.length === 0 ? placeholder : t('varietyChooser.addMore')"
          @keydown.enter.prevent="add"
          @keydown.comma.prevent="add"
          @keydown.backspace="onBackspace"
        />
        <button v-if="draft.trim()" class="vc-add-btn" type="button" @click="add">+</button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()

const props = defineProps({
  modelValue: { type: Array, default: () => [] },
  placeholder: { type: String, default: 'Es. Frantoio, Leccino...' }
})
const emit = defineEmits(['update:modelValue'])

const draft = ref('')
const inputRef = ref(null)

function normalize(val) {
  if (!val) return []
  if (typeof val === 'string') return val.trim() ? [{ name: val.trim() }] : []
  if (Array.isArray(val)) return val
  return []
}

const varieties = computed(() => normalize(props.modelValue))

function add() {
  const name = draft.value.trim()
  if (!name) return
  if (varieties.value.some(v => v.name.toLowerCase() === name.toLowerCase())) {
    draft.value = ''
    return
  }
  emit('update:modelValue', [...varieties.value, { name }])
  draft.value = ''
}

function remove(i) {
  const next = varieties.value.filter((_, idx) => idx !== i)
  emit('update:modelValue', next)
}

function onBackspace() {
  if (draft.value === '' && varieties.value.length > 0) {
    remove(varieties.value.length - 1)
  }
}
</script>

<style scoped>
.vc-wrap {
  border: 1px solid var(--border-color, #e2e8f0);
  border-radius: var(--border-radius, 8px);
  background: rgba(255,255,255,0.5);
  padding: 0.35rem 0.5rem;
  cursor: text;
  transition: border-color 0.15s;
}

.vc-wrap:focus-within {
  border-color: var(--primary-color, #4299e1);
}

.vc-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 0.3rem;
  align-items: center;
  min-height: 1.8rem;
}

.vc-chip {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.18rem 0.5rem;
  background: var(--primary-color, #4299e1);
  color: #fff;
  border-radius: 20px;
  font-size: 0.78rem;
  font-weight: 500;
  white-space: nowrap;
}

.vc-chip-remove {
  background: none;
  border: none;
  color: rgba(255,255,255,0.8);
  cursor: pointer;
  padding: 0;
  font-size: 0.9rem;
  line-height: 1;
  margin-left: 1px;
}
.vc-chip-remove:hover { color: #fff; }

.vc-input-wrap {
  display: flex;
  align-items: center;
  gap: 0.2rem;
  flex: 1;
  min-width: 80px;
}

.vc-input {
  border: none;
  outline: none;
  background: transparent;
  font-size: 0.875rem;
  color: var(--text-color, #2d3748);
  width: 100%;
  padding: 0.1rem 0;
  font-family: var(--font-family);
}

.vc-input::placeholder { color: #a0aec0; }

.vc-add-btn {
  background: var(--primary-color, #4299e1);
  border: none;
  color: #fff;
  cursor: pointer;
  font-size: 1rem;
  font-weight: 700;
  line-height: 1;
  padding: 0.1rem 0.45rem;
  border-radius: 4px;
  flex-shrink: 0;
  transition: background 0.12s;
}
.vc-add-btn:hover { background: #2b6cb0; }
</style>
